import React, { lazy, ReactNode, Suspense, useRef, useState } from 'react';

import { UnverifiedCompanyType } from '../../../../../common/constants';
import { useUnverifiedCompanyRestrictionModal } from '../../../../../common/hooks/useUnverifiedCompanyRestrictionModal/useUnverifiedCompanyRestrictionModal';
import { EmployerPurchaseProductType } from '../../../../../common/interfaces/entities';
import ErrorBoundaryWithCrashReporting from '../../../../../components/Error/ErrorBoundaryWithCrashReporting';
import useShowCreditProductPurchaseSuccessModal from '../../CreditProductPurchaseSuccessModal/useShowCreditProductPurchaseSuccessModal';
import { useListenCreditProductPurchaseComplete } from '../../purchaseModalBroadcastChannels';
import { CreditProductPurchaseOrderForAfterPayment } from '../../types';
import {
  GlintsVIPPurchaseModalContext,
  OpenModalOptions,
} from './GlintsVIPPurchaseModalContext';

const GlintsVIPFreeTrialOrPurchaseModal = lazy(
  () =>
    import(
      '../../../../../components/VIPFreeTrial/VIPFreeTrialModal/VIPFreeTrialOrPurchaseModal'
    )
);

const VIPAfterPaymentStateHandlers = lazy(
  () => import('../../../SharedComponents/VIPAfterPaymentStateHandlers')
);

type ProviderProps = {
  children: ReactNode;
};

export const GlintsVIPPurchaseModalProvider = ({ children }: ProviderProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [errorBannerProviderInitialValue, setErrorBannerProviderInitialValue] =
    useState<OpenModalOptions['errorBannerProviderInitialValue']>(undefined);
  const onCloseCallbackRef = useRef<OpenModalOptions['onClose']>(undefined);
  const onCloseCallback = onCloseCallbackRef.current;

  const {
    creditProductPurchaseSuccessModal,
    openCreditProductPurchaseSuccessModal,
  } = useShowCreditProductPurchaseSuccessModal();

  const { getOpenRestrictionModalFunc } = useUnverifiedCompanyRestrictionModal({
    type: UnverifiedCompanyType.FEATURE_PURCHASE,
  });
  const openRestrictionModalFunc = getOpenRestrictionModalFunc();

  const openGlintsVIPPurchaseModal = ({
    callback,
    errorBannerProviderInitialValue,
    onClose,
  }: OpenModalOptions = {}) => {
    if (isModalOpen) {
      return;
    }
    if (openRestrictionModalFunc) {
      return openRestrictionModalFunc();
    }
    setErrorBannerProviderInitialValue(errorBannerProviderInitialValue);
    onCloseCallbackRef.current = onClose;

    setIsModalOpen(true);
    return callback?.();
  };

  const handleCloseModal = (
    creditProductPurchaseOrder?: CreditProductPurchaseOrderForAfterPayment
  ) => {
    onCloseCallback?.(creditProductPurchaseOrder);
    if (creditProductPurchaseOrder) {
      openCreditProductPurchaseSuccessModal(creditProductPurchaseOrder);
    }
    onCloseCallbackRef.current = undefined;
    setErrorBannerProviderInitialValue(undefined);
    setIsModalOpen(false);
  };

  useListenCreditProductPurchaseComplete(
    EmployerPurchaseProductType.VIP_MEMBERSHIP,
    handleCloseModal
  );

  return (
    <GlintsVIPPurchaseModalContext.Provider
      value={{
        openGlintsVIPPurchaseModal,
        isModalChunkLoadPending: isModalOpen,
      }}
    >
      <ErrorBoundaryWithCrashReporting fallback={null}>
        <Suspense fallback={null}>
          <VIPAfterPaymentStateHandlers />
          {creditProductPurchaseSuccessModal}
        </Suspense>
        {isModalOpen && (
          <Suspense fallback={null}>
            <GlintsVIPFreeTrialOrPurchaseModal
              errorBannerProviderInitialValue={errorBannerProviderInitialValue}
              onClose={handleCloseModal}
            />
          </Suspense>
        )}
      </ErrorBoundaryWithCrashReporting>
      {children}
    </GlintsVIPPurchaseModalContext.Provider>
  );
};
